// WebGL2 renderer: every segment is an instanced screen-space quad expanded
// in the vertex shader; the fragment shader draws a capsule (round caps and
// joins, 1 px analytic AA) and evaluates the gradient per fragment from a
// baked 1D ramp texture. Geometry is uploaded once per export; level of
// detail is a cached decimated copy per stride.
import type { CameraState, CurveGeometry, GradientState, RGB } from '../../types.ts';
import type { FrameParams, OverlayLines, Renderer, RenderStats } from '../renderer.ts';
import { quatToMat3 } from '../camera.ts';
import { RampCache, helixTurns, hexToRgb } from '../../gradient/ramp.ts';
import { HELIX } from '../../gradient/helix.ts';
import { bakeRamp } from '../../gradient/bake.ts';
import { compileProgram, Uniforms } from './program.ts';
import { FRAG, VERT } from './shaders.ts';
import { segmentWindow } from '../range.ts';

interface Upload {
  buf: WebGLBuffer;
  nPts: number;     // points in this buffer (incl. closing copy)
  stride: number;   // original index step
}

export class WebGLRenderer implements Renderer {
  readonly kind = 'webgl2' as const;
  readonly canvas: HTMLCanvasElement;
  readonly budget = { settled: Infinity, interact: 3_000_000 };
  private gl: WebGL2RenderingContext;
  private prog!: WebGLProgram;
  private u!: Uniforms;
  private vao!: WebGLVertexArrayObject;
  private rampTex!: WebGLTexture;
  private rampKey = '';
  private rampW = 1;
  private rampM = 1;
  private maxTex = 4096;
  private maxRB = 4096;
  private geom: CurveGeometry | null = null;
  private center: [number, number, number] = [0, 0, 0];
  private centered: Float32Array | null = null;  // centered coords incl. closing copy
  private uploads = new Map<number, Upload>();
  /** Overlay segment buffers (centred like the curve), keyed by the coords array identity. */
  private overlayBufs = new Map<Float32Array, { buf: WebGLBuffer; n: number }>();
  private dpr = 1;
  private lost = false;
  private ramp = new RampCache();
  onContextRestored: (() => void) | null = null;

  /** Returns the renderer, or a human-readable reason it could not be created. */
  static create(canvas: HTMLCanvasElement): { renderer: WebGLRenderer } | { error: string } {
    let ctxError = '';
    const onErr = (ev: Event) => { ctxError = (ev as WebGLContextEvent).statusMessage || ''; };
    canvas.addEventListener('webglcontextcreationerror', onErr);
    const gl = canvas.getContext('webgl2', {
      alpha: false, antialias: false, depth: false, stencil: false,
      premultipliedAlpha: true, preserveDrawingBuffer: false, powerPreference: 'high-performance',
    });
    canvas.removeEventListener('webglcontextcreationerror', onErr);
    if (!gl) {
      const hasGl1 = !!document.createElement('canvas').getContext('webgl');
      return { error: 'browser refused a WebGL2 context' + (ctxError ? ` (${ctxError})` : '') + (hasGl1 ? '; WebGL1 works, WebGL2 does not' : '; no WebGL at all, likely no GPU (remote desktop, VM, or hardware acceleration disabled)') };
    }
    try {
      const r = new WebGLRenderer(canvas, gl);
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      r.gpuInfo = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER));
      return { renderer: r };
    } catch (e) {
      console.warn('WebGL2 init failed', e);
      return { error: 'WebGL2 context ok but init failed: ' + (e instanceof Error ? e.message : String(e)) };
    }
  }
  gpuInfo = '';

  private constructor(canvas: HTMLCanvasElement, gl: WebGL2RenderingContext) {
    this.canvas = canvas;
    this.gl = gl;
    this.init();
    canvas.addEventListener('webglcontextlost', (ev) => { ev.preventDefault(); this.lost = true; });
    canvas.addEventListener('webglcontextrestored', () => {
      this.lost = false;
      this.uploads.clear();
      this.rampKey = '';
      this.init();
      this.onContextRestored?.();
    });
  }

  private init(): void {
    const gl = this.gl;
    this.maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    this.maxRB = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number;
    this.prog = compileProgram(gl, VERT, FRAG);
    this.u = new Uniforms(gl, this.prog);
    this.vao = gl.createVertexArray()!;
    this.rampTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.rampTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  setGeometry(g: CurveGeometry | null): void {
    for (const up of this.uploads.values()) this.gl.deleteBuffer(up.buf);
    this.uploads.clear();
    this.clearOverlays();
    this.geom = g;
    this.centered = null;
    if (!g) return;
    const D = g.dims;
    const c: [number, number, number] = [
      (g.bbox.min[0] + g.bbox.max[0]) / 2,
      (g.bbox.min[1] + g.bbox.max[1]) / 2,
      D === 3 ? (g.bbox.min[2] + g.bbox.max[2]) / 2 : 0,
    ];
    this.center = c;
    const nEff = g.nPoints + (g.closed ? 1 : 0);
    const out = new Float32Array(nEff * D);
    const src = g.coords;
    for (let i = 0, j = 0; i < g.nPoints * D; i += D, j += D) {
      out[j] = src[i] - c[0];
      out[j + 1] = src[i + 1] - c[1];
      if (D === 3) out[j + 2] = src[i + 2] - c[2];
    }
    if (g.closed) for (let d = 0; d < D; d++) out[g.nPoints * D + d] = out[d];
    this.centered = out;
  }

  /** Buffer for a given stride: the full array for 1, else a cached decimation (always keeps the last point). */
  private uploadFor(stride: number): Upload | null {
    const g = this.geom, src = this.centered;
    if (!g || !src) return null;
    let up = this.uploads.get(stride);
    if (up) return up;
    const D = g.dims;
    const nEff = src.length / D;
    let data: Float32Array;
    let nPts: number;
    if (stride <= 1) {
      data = src; nPts = nEff;
    } else {
      const m = Math.floor((nEff - 1) / stride) + 1;
      const tailIsEnd = (m - 1) * stride === nEff - 1;
      nPts = tailIsEnd ? m : m + 1;
      data = new Float32Array(nPts * D);
      for (let k = 0; k < nPts; k++) {
        const si = (k < m ? k * stride : nEff - 1) * D;
        for (let d = 0; d < D; d++) data[k * D + d] = src[si + d];
      }
    }
    const gl = this.gl;
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    up = { buf, nPts, stride };
    // Keep GPU memory bounded: full + at most 3 decimations.
    if (this.uploads.size >= 4) {
      for (const [k, v] of this.uploads) { if (k !== 1) { gl.deleteBuffer(v.buf); this.uploads.delete(k); break; } }
    }
    this.uploads.set(stride, up);
    return up;
  }

  private clearOverlays(): void {
    for (const o of this.overlayBufs.values()) this.gl.deleteBuffer(o.buf);
    this.overlayBufs.clear();
  }

  /** Buffer of centred segment pairs for an overlay; cached by array identity (at most 6). */
  private overlayFor(o: OverlayLines): { buf: WebGLBuffer; n: number } | null {
    let e = this.overlayBufs.get(o.coords);
    if (e) return e;
    const D = o.dims, n = o.coords.length / (2 * D);
    if (!Number.isInteger(n) || n <= 0) return null;
    const c = this.center;
    const data = new Float32Array(o.coords.length);
    for (let i = 0; i < o.coords.length; i += D) {
      data[i] = o.coords[i] - c[0];
      data[i + 1] = o.coords[i + 1] - c[1];
      if (D === 3) data[i + 2] = o.coords[i + 2] - c[2];
    }
    const gl = this.gl;
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    e = { buf, n };
    if (this.overlayBufs.size >= 6) { const k = this.overlayBufs.keys().next().value as Float32Array; gl.deleteBuffer(this.overlayBufs.get(k)!.buf); this.overlayBufs.delete(k); }
    this.overlayBufs.set(o.coords, e);
    return e;
  }

  /** Draw disjoint segments in a solid colour. Scene uniforms must already be set. */
  private drawOverlays(p: FrameParams, dpr: number, widthScale: number): void {
    const gl = this.gl, u = this.u;
    for (const o of p.overlays) {
      const e = this.overlayFor(o);
      if (!e) continue;
      const D = o.dims, bstride = D * 4;
      gl.bindVertexArray(this.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, e.buf);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, D, gl.FLOAT, false, 2 * bstride, 0);
      gl.vertexAttribDivisor(0, 1);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, D, gl.FLOAT, false, 2 * bstride, bstride);
      gl.vertexAttribDivisor(1, 1);
      const wDev = o.width * widthScale * dpr;
      u.i('u_mode', 2);
      u.f3('u_solid', o.color[0] / 255, o.color[1] / 255, o.color[2] / 255);
      u.f('u_alpha', o.alpha);
      u.i('u_depthCue', 0);
      u.f('u_halfWidth', Math.max(0.75, wDev / 2));
      u.f('u_alphaScale', Math.min(1, wDev / 1.5));
      u.f('u_stride', 1);
      u.f('u_invNm1', 1);
      u.f('u_first', 0);
      u.f('u_lastInst', e.n - 1);
      u.f2('u_clip', 0, 1);
      gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, e.n);
      gl.bindVertexArray(null);
    }
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.dpr = dpr;
    this.canvas.width = Math.max(2, Math.floor(cssW * dpr));
    this.canvas.height = Math.max(2, Math.floor(cssH * dpr));
  }

  private updateRamp(p: FrameParams): void {
    const g = p.grad;
    const key = g.stops.join(',') + '|' + (g.reverse ? 1 : 0) + '|' + (p.cyclic ? 1 : 0) + '|' + g.closure;
    if (key === this.rampKey) return;
    const stops = this.ramp.get(g, p.cyclic);
    const baked = bakeRamp(stops, this.maxTex);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.rampTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, baked.width, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, baked.rgba);
    this.rampKey = key;
    this.rampW = baked.width;
    this.rampM = baked.m;
  }

  private setSceneUniforms(p: FrameParams, cam: CameraState, viewW: number, viewH: number, dpr: number, devW: number, devH: number, lineWidthCss: number): void {
    const u = this.u;
    const c = this.center;
    const k = p.aspectK;
    // World (centered) point at the viewport centre, in the post-rotation/stretch frame.
    let vcx: number, vcy: number;
    if (p.is3d) {
      // Rotation pivots on the bbox centre: the buffer holds p - c, so the
      // viewport-centre point is expressed directly in the rotated frame.
      vcx = (viewW / 2 - cam.tx) / cam.scale;
      vcy = (viewH / 2 - cam.ty) / cam.scale;
      u.m3('u_rot', quatToMat3(cam.rot));
    } else {
      vcx = (viewW / 2 - cam.tx) / cam.scale - c[0] * k;
      vcy = (viewH / 2 - cam.ty) / cam.scale + c[1] / k;
    }
    u.i('u_is3d', p.is3d ? 1 : 0);
    u.f2('u_axy', k, 1 / k);
    u.f('u_scale', cam.scale);
    u.f2('u_vc', vcx, vcy);
    u.f2('u_centerPx', viewW / 2, viewH / 2);
    u.f('u_dpr', dpr);
    u.f2('u_viewport', devW, devH);
    const wDev = lineWidthCss * dpr;
    u.f('u_halfWidth', Math.max(0.75, wDev / 2));
    u.f('u_alphaScale', Math.min(1, wDev / 1.5));
    const v3 = p.view3d;
    u.i('u_persp', p.is3d && v3.perspective ? 1 : 0);
    u.f('u_invExtent', 1 / Math.max(1e-9, v3.extent));
    u.i('u_depthCue', p.is3d && v3.depthCue ? 1 : 0);
    u.f('u_alpha', p.is3d ? v3.opacity : 1);

    const g: GradientState = p.grad;
    const mode = p.colorMode === 'solid' ? 2 : p.colorMode === 'helix' ? 1 : 0;
    u.i('u_mode', mode);
    u.i('u_ramp', 0);
    u.f('u_rampW', this.rampW);
    u.f('u_m', this.rampM);
    u.f('u_reps', Math.max(1, g.repeat | 0));
    u.f('u_rampRot', g.rotation || 0);
    u.i('u_pingpong', g.pingpong ? 1 : 0);
    u.i('u_hard', g.hard ? 1 : 0);
    u.f4('u_helix', HELIX.Lmin, HELIX.Lmax, HELIX.C, helixTurns(g.repeat));
    const s: RGB = hexToRgb(p.solidHex || '#000000');
    u.f3('u_solid', s[0] / 255, s[1] / 255, s[2] / 255);
  }

  private clear(bgHex: string, transparent: boolean): void {
    const gl = this.gl;
    if (transparent) gl.clearColor(0, 0, 0, 0);
    else { const c = hexToRgb(bgHex || '#ffffff'); gl.clearColor(c[0] / 255, c[1] / 255, c[2] / 255, 1); }
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  /** Draws the part of `p.range` that falls in this buffer; returns the number of segments drawn. */
  private drawSegments(up: Upload, D: number, nPoints: number, p: FrameParams): number {
    const gl = this.gl;
    const total = (this.centered ? this.centered.length / D : nPoints) - 1;   // segments at stride 1, incl. the closing one
    const win = segmentWindow(p.range, total, up.stride, up.nPts - 1);
    if (win.count <= 0) return 0;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, up.buf);
    const bstride = D * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, D, gl.FLOAT, false, bstride, win.first * bstride);
    gl.vertexAttribDivisor(0, 1);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, D, gl.FLOAT, false, bstride, (win.first + 1) * bstride);
    gl.vertexAttribDivisor(1, 1);
    this.u.f('u_stride', up.stride);
    this.u.f('u_invNm1', 1 / Math.max(1, nPoints - 1));
    this.u.f('u_first', win.first);
    this.u.f('u_lastInst', win.count - 1);
    this.u.f2('u_clip', win.lo, win.hi);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, win.count);
    gl.bindVertexArray(null);
    return win.count;
  }

  draw(p: FrameParams): RenderStats {
    const gl = this.gl;
    const g = this.geom;
    if (this.lost) return { drawn: 0, total: g?.nPoints ?? 0, stride: p.stride };
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.clear(p.bgHex, false);
    if (!g) return { drawn: 0, total: 0, stride: 1 };
    const stride = Math.max(1, p.stride | 0);
    const up = this.uploadFor(stride);
    if (!up) return { drawn: 0, total: g.nPoints, stride };
    gl.useProgram(this.prog);
    this.updateRamp(p);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.rampTex);
    this.setSceneUniforms(p, p.cam, p.viewW, p.viewH, this.dpr, this.canvas.width, this.canvas.height, p.lineWidth);
    // Tile outlines sit behind the curve, dots and the highlighted tile in front.
    const behind = p.overlays.filter((o) => o.width < 2), front = p.overlays.filter((o) => o.width >= 2);
    if (behind.length) { this.drawOverlays({ ...p, overlays: behind }, this.dpr, 1); this.setSceneUniforms(p, p.cam, p.viewW, p.viewH, this.dpr, this.canvas.width, this.canvas.height, p.lineWidth); }
    const segs = this.drawSegments(up, g.dims, g.nPoints, p);
    if (front.length) this.drawOverlays({ ...p, overlays: front }, this.dpr, 1);
    return { drawn: Math.min(g.nPoints, segs + 1), total: g.nPoints, stride };
  }

  async renderRegion(p: FrameParams, rx: number, ry: number, rw: number, rh: number, scale: number,
                     onProgress?: (done: number, total: number) => void): Promise<HTMLCanvasElement> {
    const gl = this.gl;
    const W = Math.max(2, Math.round(rw * scale));
    const H = Math.max(2, Math.round(rh * scale));
    const out = document.createElement('canvas');
    out.width = W; out.height = H;
    const g2 = out.getContext('2d');
    if (!g2) throw new Error('Could not create export canvas');
    const g = this.geom;
    if (!g || this.lost) return out;

    // Export camera in CSS px of the export image (dpr = 1).
    const cam2: CameraState = {
      scale: p.cam.scale * scale,
      tx: (p.cam.tx - rx) * scale,
      ty: (p.cam.ty - ry) * scale,
      rot: p.cam.rot,
    };
    const T = Math.min(4096, this.maxRB, this.maxTex);
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, T, T, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fbo); gl.deleteTexture(tex);
      throw new Error('Export framebuffer incomplete');
    }

    const stride = Math.max(1, p.stride | 0);
    const up = this.uploadFor(stride);
    const tilesX = Math.ceil(W / T), tilesY = Math.ceil(H / T);
    const total = tilesX * tilesY;
    let done = 0;
    const pixels = new Uint8ClampedArray(T * T * 4);
    try {
      for (let ty = 0; ty < tilesY; ty++) {
        for (let tx = 0; tx < tilesX; tx++) {
          const ox = tx * T, oy = ty * T;
          const tw = Math.min(T, W - ox), th = Math.min(T, H - oy);
          gl.viewport(0, 0, tw, th);
          this.clear(p.bgHex, p.transparent);
          if (up) {
            gl.useProgram(this.prog);
            this.updateRamp(p);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.rampTex);
            // Shift the camera so this tile's origin is at (0,0); viewport centre is the tile centre.
            const camTile: CameraState = { ...cam2, tx: cam2.tx - ox, ty: cam2.ty - oy };
            this.setSceneUniforms(p, camTile, tw, th, 1, tw, th, p.lineWidth * scale);
            const behind = p.overlays.filter((o) => o.width < 2), front = p.overlays.filter((o) => o.width >= 2);
            if (behind.length) { this.drawOverlays({ ...p, overlays: behind }, 1, scale); this.setSceneUniforms(p, camTile, tw, th, 1, tw, th, p.lineWidth * scale); }
            this.drawSegments(up, g.dims, g.nPoints, p);
            if (front.length) this.drawOverlays({ ...p, overlays: front }, 1, scale);
          }
          const view = new Uint8ClampedArray(pixels.buffer, 0, tw * th * 4);
          gl.readPixels(0, 0, tw, th, gl.RGBA, gl.UNSIGNED_BYTE, view as unknown as Uint8Array);
          // Flip rows (GL origin is bottom-left) and un-premultiply for putImageData.
          const img = new ImageData(tw, th);
          const dst = img.data;
          const rowBytes = tw * 4;
          for (let y = 0; y < th; y++) {
            const srcOff = (th - 1 - y) * rowBytes, dstOff = y * rowBytes;
            for (let i = 0; i < rowBytes; i += 4) {
              const a = view[srcOff + i + 3];
              if (a === 0 || a === 255) {
                dst[dstOff + i] = view[srcOff + i]; dst[dstOff + i + 1] = view[srcOff + i + 1]; dst[dstOff + i + 2] = view[srcOff + i + 2];
              } else {
                const f = 255 / a;
                dst[dstOff + i] = view[srcOff + i] * f; dst[dstOff + i + 1] = view[srcOff + i + 1] * f; dst[dstOff + i + 2] = view[srcOff + i + 2] * f;
              }
              dst[dstOff + i + 3] = a;
            }
          }
          g2.putImageData(img, ox, oy);
          done++;
          onProgress?.(done, total);
          if (total > 1) await new Promise((r) => setTimeout(r, 0));
        }
      }
    } finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fbo);
      gl.deleteTexture(tex);
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }
    return out;
  }

  dispose(): void {
    for (const up of this.uploads.values()) this.gl.deleteBuffer(up.buf);
    this.uploads.clear();
    this.clearOverlays();
    this.gl.deleteTexture(this.rampTex);
    this.gl.deleteProgram(this.prog);
    this.geom = null;
  }
}
